import apiClient from '@/api/client';
import type { PaginatedResponse } from '@/api/endpoints/accounting';

// ============================================================
// EMPLOYEES
// ============================================================
export interface EmployeeResponse {
  id: string;
  employee_ref: string;
  company_id: string;
  department_id: string | null;
  company_name: string | null;
  company_logo_url: string | null;
  department_name: string | null;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email: string | null;
  phone: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  designation: string | null;
  supervisor_id: string | null;
  // Personal
  date_of_birth: string | null;
  gender: string | null;
  marital_status: string | null;
  nationality: string | null;
  state_of_origin: string | null;
  nin: string | null;
  blood_group: string | null;
  religion: string | null;
  disability_status: string | null;
  bio: string | null;
  avatar_url: string | null;
  // Social media
  linkedin_url: string | null;
  twitter_url: string | null;
  github_url: string | null;
  // Work info
  work_location: string | null;
  // Structured profile
  education: { institution: string; degree: string; field: string; year: number | null }[];
  certifications: { name: string; issuer: string; date: string | null; expiry: string | null }[];
  skills: string[];
  languages_spoken: { language: string; proficiency: string }[];
  // Contact
  personal_email: string | null;
  work_email: string | null;
  personal_phone: string | null;
  city: string | null;
  lga: string | null;
  state: string | null;
  tax_state: string | null;
  tax_state_code: string | null;
  country: string | null;
  postal_code: string | null;
  // Emergency / Next of Kin
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relationship: string | null;
  emergency_contact_address_line_1: string | null;
  emergency_contact_address_line_2: string | null;
  emergency_contact_city: string | null;
  emergency_contact_lga: string | null;
  emergency_contact_state: string | null;
  emergency_contact_postal_code: string | null;
  emergency_contact_country: string | null;
  // Preferences
  comm_preferences: { email?: boolean; sms?: boolean; whatsapp?: boolean; push?: boolean };
  two_factor_enabled: boolean;
  two_factor_method: string | null;
  // Banking
  bank_name: string | null;
  bank_code: string | null;
  bank_account_number: string | null;
  // Employment
  date_of_hire: string;
  date_of_exit: string | null;
  exit_reason: string | null;
  probation_end_date: string | null;
  notice_period_end: string | null;
  employment_status: string;
  pay_frequency: string | null;
  user_id: string | null;
  has_login: boolean;
  /** Self-service profile lock — TRUE blocks PATCH /employee/my-profile; HR toggles it. */
  profile_locked: boolean;
  // Salary
  basic_salary: string;
  housing_allowance: string;
  transport_allowance: string;
  meal_allowance: string;
  other_allowances: Record<string, unknown>;
  // Statutory
  pension_employee_pct: string;
  pension_employer_pct: string;
  nhf_pct: string;
  nsitf_pct: string;
  pfa_name: string | null;
  pfa_pin: string | null;
  tax_id: string | null;
  // P01 PCRS — PenCom pension compliance
  pension_rsa_pin: string | null;
  pension_pfa_code: string | null;
  pension_pfa_member_number: string | null;
  // Phase 9 — NTA 2026
  annual_rent_paid: string | null;
  nhf_opt_in: boolean;
  is_resident_taxable_globally: boolean;
  bvn: string | null;
  nhis_id: string | null;
  // T2-GAP11 — BIK: benefit name → monthly value (string)
  bik_items: Record<string, string> | null;
  // Gig worker wallet fields
  wallet_provider: string | null;
  wallet_id: string | null;
  wallet_tier: number | null;
  // International payment fields
  swift_bic: string | null;
  iban_or_account: string | null;
  created_at: string;
  /** Pay-date of the most recent payroll run that paid this employee. */
  last_paid_date: string | null;
}

export interface SalaryHistoryEntry {
  id: string;
  employee_id: string;
  effective_date: string;
  basic_salary: string;
  housing_allowance: string;
  transport_allowance: string;
  meal_allowance: string;
  reason: string | null;
  created_at: string;
}

export interface PayTrendEntry {
  year: number;
  month: number;
  gross_pay: string;
  net_pay: string;
  paye: string;
  total_deductions: string;
  pension_employee: string;
  nhf: string;
  run_id: string | null;
}

export interface MonthlyDeductionsResponse {
  gross_monthly: string;
  pension_base: string;
  paye_monthly: string;
  pension_employee_monthly: string;
  nhf_monthly: string;
  nsitf_monthly: string;
  itf_monthly: string;
  total_employee_deductions: string;
  pension_employer_monthly: string;
  nsitf_employer_monthly: string;
  itf_employer_monthly: string;
  total_employer_contribution: string;
  net_monthly: string;
  total_cost_to_company: string;
  itf_eligible: boolean;
  paye_annual: string;
  cra_annual: string;
  minimum_tax_annual: string;
  minimum_tax_applied: boolean;
}

export interface HeadcountResponse {
  year: number;
  monthly: { month: number; count: number }[];
  departments: { department_id: string | null; department_name: string; count: number }[];
  current_total: number;
}

export interface EmployeeCreateRequest {
  first_name: string;
  middle_name?: string;
  last_name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  gender?: string;
  marital_status?: string;
  nationality?: string;
  state_of_origin?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  lga?: string;
  state?: string;
  tax_state?: string;
  // Canonical 2-letter PAYE-routing state code (NTAA 2025 s.87). Blank = NRS federal.
  tax_state_code?: string;
  postal_code?: string;
  country?: string;
  designation?: string;
  department_id?: string;
  supervisor_id?: string;
  date_of_hire: string;
  employment_status?: string;
  pay_frequency?: string;
  probation_end_date?: string;
  basic_salary: number;
  housing_allowance?: number;
  transport_allowance?: number;
  meal_allowance?: number;
  other_allowances?: Record<string, unknown>;
  // Bank (payroll disbursement)
  bank_name?: string;
  bank_code?: string;
  bank_account_number?: string;
  // Pension / statutory
  pfa_name?: string;
  pfa_pin?: string;
  pension_employee_pct?: number;
  pension_employer_pct?: number;
  nhf_pct?: number;
  nsitf_pct?: number;
  tax_id?: string;
  nin?: string;
  // Phase 9 — NTA 2026
  annual_rent_paid?: number | null;
  nhf_opt_in?: boolean;
  is_resident_taxable_globally?: boolean;
  bvn?: string;
  nhis_id?: string;
  cost_allocations?: { company_id: string; percentage: number }[];
  create_login?: boolean;
  login_password?: string;
}

export interface EmployeeKPIs {
  total: number;
  active: number;
  probation: number;
  onboarding: number;
  on_leave: number;
  new_hires_month: number;
  new_hires_quarter: number;
  turnover_rate: number;
  exits_12m: number;
  avg_tenure_months: number;
}

export const employeesApi = {
  /** List active departments from the canonical departments table (not reference_data).
   *  Use this for the employee-creation wizard dropdown so that department_id
   *  FK on employees resolves correctly. */
  listDepartments: () =>
    apiClient.get<{ items: { id: string; name: string }[] }>('/hr/departments'),
  kpis: () =>
    apiClient.get<EmployeeKPIs>('/hr/employees/kpis'),
  list: (params?: { department_id?: string; status?: string; search?: string; page?: number; per_page?: number }) =>
    apiClient.get<PaginatedResponse<EmployeeResponse>>('/hr/employees', { params }),
  get: (id: string) =>
    // Detail page renders its own not-found / error state, so suppress the
    // global error toast to avoid double-messaging the user.
    apiClient.get<EmployeeResponse>(`/hr/employees/${id}`, { _silentOnError: true } as any),
  create: (data: EmployeeCreateRequest) =>
    apiClient.post<EmployeeResponse>('/hr/employees', data),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch<EmployeeResponse>(`/hr/employees/${id}`, data),
  getSalaryHistory: (id: string) =>
    apiClient.get<SalaryHistoryEntry[]>(`/hr/employees/${id}/salary-history`),
  getLeaveBalances: (id: string, year?: number) =>
    apiClient.get<LeaveBalanceEntry[]>(`/hr/employees/${id}/leave-balances`, { params: year ? { year } : {} }),
  transition: (id: string, newStatus: string, reason?: string) =>
    apiClient.post<EmployeeResponse>(`/hr/employees/${id}/transition`, null, { params: { new_status: newStatus, reason } }),
  createLogin: (id: string, password: string) =>
    apiClient.post<EmployeeResponse>(`/hr/employees/${id}/create-login`, { password }),
  getMonthlyDeductions: (id: string) =>
    apiClient.get<MonthlyDeductionsResponse>(`/hr/employees/${id}/monthly-deductions`),
  getPayTrend: (id: string, months = 12) =>
    apiClient.get<PayTrendEntry[]>(`/hr/employees/${id}/pay-trend`, { params: { months } }),
  getOnboarding: (id: string) =>
    apiClient.get<OnboardingInstance | null>(`/hr/employees/${id}/onboarding`),
  toggleProfileLock: (id: string, locked: boolean) =>
    apiClient.patch<{ id: string; profile_locked: boolean }>(`/hr/employees/${id}/profile-lock`, { locked }),
  uploadAvatar: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<{ avatar_url: string }>(`/hr/employees/${id}/avatar`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateBik: (id: string, bikItems: Record<string, number>) =>
    apiClient.put<{ employee_id: string; bik_items: Record<string, string>; monthly_total: number; message: string }>(
      `/hr/employees/${id}/bik`,
      { bik_items: bikItems },
    ),
  updateWallet: (id: string, data: { wallet_provider?: string; wallet_id?: string; wallet_tier?: number }) =>
    apiClient.patch(`/hr/employees/${id}`, data),
};

// ============================================================
// EMPLOYEE TRANSFERS
// ============================================================
export interface EmployeeTransferResponse {
  id: string;
  employee_id: string;
  employee_name: string | null;
  from_company_id: string;
  from_company_name: string | null;
  to_company_id: string;
  to_company_name: string | null;
  transfer_type: 'permanent' | 'secondment';
  effective_date: string;
  reason: string | null;
  status: 'pending' | 'approved' | 'completed' | 'cancelled';
  leave_action: 'transfer' | 'payout' | 'forfeit';
  deduction_action: 'transfer' | 'close';
  new_department_id: string | null;
  new_department_name: string | null;
  new_supervisor_id: string | null;
  new_designation: string | null;
  new_basic_salary: string | null;
  new_housing_allowance: string | null;
  new_transport_allowance: string | null;
  new_meal_allowance: string | null;
  initiated_by: string;
  initiated_by_name: string | null;
  approved_by: string | null;
  approved_at: string | null;
  completed_at: string | null;
  notes: string | null;
  old_employee_ref: string | null;
  new_employee_ref: string | null;
  created_at: string | null;
}

export interface CostAllocationEntry {
  company_id: string;
  company_name: string | null;
  percentage: string;
  updated_at: string | null;
}

export interface EmployeeTransferCreateRequest {
  to_company_id: string;
  transfer_type: 'permanent' | 'secondment';
  effective_date: string;
  reason?: string;
  leave_action?: 'transfer' | 'payout' | 'forfeit';
  deduction_action?: 'transfer' | 'close';
  new_department_id?: string;
  new_supervisor_id?: string;
  new_designation?: string;
  new_basic_salary?: number;
  new_housing_allowance?: number;
  new_transport_allowance?: number;
  new_meal_allowance?: number;
  notes?: string;
}

export const transferApi = {
  initiate: (employeeId: string, data: EmployeeTransferCreateRequest) =>
    apiClient.post<EmployeeTransferResponse>(`/hr/employees/${employeeId}/transfers`, data),
  complete: (transferId: string) =>
    apiClient.post<EmployeeTransferResponse>(`/hr/transfers/${transferId}/complete`),
  cancel: (transferId: string) =>
    apiClient.post<EmployeeTransferResponse>(`/hr/transfers/${transferId}/cancel`),
  getHistory: (employeeId: string) =>
    apiClient.get<EmployeeTransferResponse[]>(`/hr/employees/${employeeId}/transfers`),
  getCostAllocations: (employeeId: string) =>
    apiClient.get<CostAllocationEntry[]>(`/hr/employees/${employeeId}/cost-allocations`),
};

// ============================================================
// LEAVE
// ============================================================
export interface LeaveTypeResponse {
  id: string;
  name: string;
  default_days: number;
  is_paid: boolean;
  max_carry_forward: number | null;
  allow_half_day: boolean;
  requires_attachment: boolean;
}

export interface LeaveRequestResponse {
  id: string;
  employee_id: string;
  employee_name?: string;
  company_id: string;
  leave_type_id: string;
  leave_type_name?: string;
  start_date: string;
  end_date: string;
  days: number;
  half_day: string | null;
  reason: string | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

export interface LeaveBalanceEntry {
  id: string;
  employee_id: string;
  leave_type_id: string;
  leave_type_name?: string;
  year: number;
  entitled_days: number;
  used_days: number;
  remaining: number;
}

export const leaveApi = {
  list: (params?: { employee_id?: string; status?: string; page?: number; per_page?: number }) =>
    apiClient.get<PaginatedResponse<LeaveRequestResponse>>('/hr/leave-requests', { params }),
  create: (employeeId: string, data: {
    leave_type_id: string; start_date: string; end_date: string;
    reason?: string; half_day?: 'morning' | 'afternoon' | null;
  }) =>
    apiClient.post<LeaveRequestResponse>(`/hr/leave-requests?employee_id=${employeeId}`, data),
  review: (id: string, status: 'approved' | 'rejected', notes?: string) =>
    apiClient.post<LeaveRequestResponse>(`/hr/leave-requests/${id}/review`, { status, notes }),
  cancel: (id: string, reason?: string) =>
    apiClient.post<LeaveRequestResponse>(`/hr/leave-requests/${id}/cancel`, { reason }),
  adjustBalance: (data: {
    employee_id: string; leave_type_id: string; year: number;
    adjustment: number; reason: string;
  }) =>
    apiClient.post<LeaveBalanceEntry>('/hr/leave-balances/adjust', data),
  listTypes: () =>
    apiClient.get<LeaveTypeResponse[]>('/hr/leave-types'),
};

// ============================================================
// EMPLOYEE PORTAL (self-service)
// ============================================================
export interface EmployeeProfileUpdate {
  bio?: string;
  personal_email?: string;
  personal_phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  lga?: string;
  state?: string;
  tax_state?: string;
  country?: string;
  postal_code?: string;
  marital_status?: string;
  blood_group?: string;
  linkedin_url?: string;
  twitter_url?: string;
  github_url?: string;
  education?: { institution: string; degree: string; field: string; year: number | null }[];
  certifications?: { name: string; issuer: string; date: string | null; expiry: string | null }[];
  skills?: string[];
  languages_spoken?: { language: string; proficiency: string }[];
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  emergency_contact_address_line_1?: string;
  emergency_contact_address_line_2?: string;
  emergency_contact_city?: string;
  emergency_contact_lga?: string;
  emergency_contact_state?: string;
  emergency_contact_postal_code?: string;
  emergency_contact_country?: string;
  comm_preferences?: Record<string, boolean>;
  bank_name?: string;
  bank_account_number?: string;
}

export const portalApi = {
  getMyProfile: () => apiClient.get<EmployeeResponse>('/employee/my-profile'),
  updateMyProfile: (data: EmployeeProfileUpdate) =>
    apiClient.patch<EmployeeResponse>('/employee/my-profile', data),
  uploadMyAvatar: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<{ avatar_url: string }>('/employee/my-avatar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ============================================================
// EMPLOYEE SELF-SERVICE — BANK / PAYMENT DETAILS
// ============================================================
export interface MyBankDetailsResponse {
  /** Masked account number — only last 4 digits exposed, e.g. "******1234". */
  bank_account_number_masked: string | null;
  bank_name: string | null;
  bank_code: string | null;
  /** True when Paystack has issued a transfer recipient code for the account. */
  is_verified: boolean;
  paystack_recipient_code: string | null;
  /** Present when a change request is awaiting HR review. */
  pending_request: {
    id: string;
    bank_name: string | null;
    bank_code: string | null;
    bank_account_number_masked: string | null;
    status: string;
    created_at: string | null;
  } | null;
}

export interface BankDetailsChangeRequest {
  bank_account_number: string;
  bank_name: string;
  bank_code: string;
}

/** A pending/reviewed employee bank-detail change request, as seen by HR. */
export interface BankDetailsRequestAdminItem {
  id: string;
  employee_id: string;
  employee_name: string;
  designation: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requested_bank_name: string | null;
  requested_bank_code: string | null;
  requested_account_number_masked: string | null;
  rejection_reason: string | null;
  created_at: string | null;
}

export const bankDetailsApi = {
  getMine: () =>
    apiClient.get<MyBankDetailsResponse>('/employees/me/bank-details'),
  // 409 from the backend means an active payroll run is locking the account.
  requestChange: (data: BankDetailsChangeRequest) =>
    apiClient.put<MyBankDetailsResponse>('/employees/me/bank-details', data, {
      _silentOnError: true,
    } as any),

  // --- HR / admin review side ---
  listRequests: (status: string = 'pending') =>
    apiClient
      .get<{ requests: BankDetailsRequestAdminItem[] }>(
        '/employees/bank-details-requests',
        { params: { status } },
      )
      .then((r) => r.data.requests),
  // decision: 'approve' | 'reject'. 409 = active payroll locking the account.
  review: (
    employeeId: string,
    requestId: string,
    decision: 'approve' | 'reject',
    rejectionReason?: string,
  ) =>
    apiClient.patch<{ id: string; status: string; rejection_reason?: string }>(
      `/employees/${employeeId}/bank-details-requests/${requestId}`,
      { decision, rejection_reason: rejectionReason },
    ),
};

// ============================================================
// PAYROLL
// ============================================================
export interface PayrollRunResponse {
  id: string;
  company_id: string | null;
  period_year: number;
  period_month: number;
  status: string;
  total_gross: string | null;
  total_net: string | null;
  total_paye: string | null;
  total_pension_ee: string | null;
  total_pension_er: string | null;
  total_nhf: string | null;
  total_nsitf: string | null;
  total_itf: string | null;
  approved_by: string | null;
  approved_at: string | null;
  voided: boolean;
  created_by: string;
  created_at: string;
  // BUG-072 — headcount for the run. Number of payroll lines once calculated;
  // for a not-yet-calculated draft, the active roster the run will pay.
  employee_count?: number | null;
}

export interface PayrollLineResponse {
  id: string;
  run_id: string;
  employee_id: string;
  company_id: string;
  employee_name: string | null;
  employee_ref: string | null;
  allocation_pct: string;
  basic_salary: string;
  housing_allowance: string;
  transport_allowance: string;
  meal_allowance: string;
  other_allowances: Record<string, unknown>;
  gross_pay: string;
  pension_employee: string;
  pension_employer: string;
  nhf: string;
  nsitf: string;
  itf: string;
  paye: string;
  other_deductions: Record<string, unknown>;
  total_deductions: string;
  net_pay: string;
}

export interface PayrollChangeLogEntry {
  id: string;
  run_id: string;
  company_id: string;
  employee_id: string | null;
  employee_name: string | null;
  employee_ref: string | null;
  field_changed: string;
  old_value: string | null;
  new_value: string | null;
  changed_by: string | null;
  changed_by_name: string | null;
  changed_at: string;
}

export interface PreflightVerdict {
  rule_id: string;
  severity: 'critical' | 'warning' | 'info';
  category: 'anomaly' | 'compliance';
  title: string;
  detail: string;
  affected_employee_ids: string[];
}

export interface PreflightReport {
  run_id: string;
  country_code: string;
  counts_by_severity: { critical: number; warning: number; info: number };
  verdicts: PreflightVerdict[];
  /** True when at least one verdict's detail was rewritten by the AI polish
   *  pass. Shown as an "AI-polished" chip in the UI so admins know the
   *  wording isn't the raw rule template.
   */
  ai_polished?: boolean;
}

// GAP-014 — self-service onboarding invite.
export interface OnboardingInviteStatus {
  invite_id: string;
  employee_id: string;
  status: 'pending' | 'submitted' | 'approved' | 'revoked' | 'expired';
  expires_at: string;
  submitted_at?: string | null;
}

// GAP-014 — a row in the HR onboarding review queue (invite joined to its hire).
export interface OnboardingInviteListItem {
  invite_id: string;
  employee_id: string;
  status: 'pending' | 'submitted' | 'approved' | 'revoked' | 'expired';
  invited_email: string;
  first_name: string;
  last_name: string;
  designation: string | null;
  expires_at: string;
  submitted_at?: string | null;
  created_at?: string | null;
}

export interface OnboardingPrefill {
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  gender: string | null;
  marital_status: string | null;
  state_of_origin: string | null;
  address_line_1: string | null;
  city: string | null;
  state: string | null;
  tax_state_code: string | null;
  bank_name: string | null;
  bank_code: string | null;
  pension_pfa_code: string | null;
  pension_pfa_member_number: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relationship: string | null;
}

export const onboardingInviteApi = {
  // Admin (authenticated)
  create: (data: {
    first_name: string;
    last_name: string;
    email: string;
    basic_salary: number;
    designation?: string;
    date_of_hire?: string;
  }) =>
    apiClient.post<{
      invite_id: string;
      employee_id: string;
      status: string;
      expires_at: string;
      email_sent: boolean;
      invite_link: string;
    }>('/hr/onboarding-invites', data),
  list: (status?: string) =>
    apiClient.get<OnboardingInviteListItem[]>('/hr/onboarding-invites', {
      params: status ? { status } : {},
    }),
  approve: (inviteId: string) =>
    apiClient.post<{
      invite_id: string;
      employee_id: string;
      status: string;
      employment_status: string;
    }>(`/hr/onboarding-invites/${inviteId}/approve`),
  revoke: (inviteId: string) =>
    apiClient.post<{ invite_id: string; status: string }>(
      `/hr/onboarding-invites/${inviteId}/revoke`,
    ),
  statusByEmployee: (employeeId: string) =>
    apiClient.get<OnboardingInviteStatus | null>(
      `/hr/onboarding-invites/by-employee/${employeeId}`,
    ),
  // Public (tokenized — runs on the unauthenticated /onboarding/complete page)
  startSession: (token: string) =>
    apiClient.post<{
      employee_id: string;
      first_name: string;
      last_name: string;
      company_id: string;
    }>('/onboarding/session', { token }),
  prefill: () => apiClient.get<OnboardingPrefill>('/onboarding/me'),
  submit: (profile: Record<string, string | null | undefined>) =>
    apiClient.post<{ status: string }>('/onboarding/submit', profile),
};

// GAP-006 — cost summary returned by GET /payroll/runs/{id}/summary.
export interface PayrollCostSummary {
  run_id: string;
  period: string;
  pay_date: string | null;
  employee_count: number;
  net_pay: number;
  statutory_due: number;
  total_cost: number;
  breakdown: {
    gross: number;
    paye: number;
    pension_employee: number;
    nhf: number;
    pension_employer: number;
    nsitf: number;
    itf: number;
    employee_withholdings: number;
    employer_statutory: number;
  };
  reconciles_to_lines: boolean;
}

export const payrollApi = {
  listRuns: (params?: { year?: number; page?: number; per_page?: number }) =>
    apiClient.get<PaginatedResponse<PayrollRunResponse>>('/payroll/runs', { params }),
  createRun: (data: { period_year: number; period_month: number; company_id?: string }) =>
    apiClient.post<PayrollRunResponse>('/payroll/runs', data),
  calculate: (runId: string, params?: { operated_company_ids?: string[] }) =>
    apiClient.post<PayrollRunResponse>(`/payroll/runs/${runId}/calculate`, undefined, {
      params,
      // Repeat the same key for each id so FastAPI's List[UUID] Query
      // parser sees them as a list, not a comma-joined string.
      paramsSerializer: { indexes: null },
    }),
  approve: (runId: string) =>
    apiClient.post<PayrollRunResponse>(`/payroll/runs/${runId}/approve`),
  // GAP-006: the "what it costs" summary — three honest figures (cash to
  // staff, statutory due to authorities, total employer cost) + breakdown.
  costSummary: (runId: string) =>
    apiClient.get<PayrollCostSummary>(`/payroll/runs/${runId}/summary`),
  // GAP-009: the resumable (draft/calculated) run for a period, if any, so the
  // wizard can offer "pick up where you left off" instead of erroring on a
  // duplicate run. Returns null when there's nothing to resume.
  inProgressRun: (year: number, month: number) =>
    apiClient.get<PayrollRunResponse | null>('/payroll/runs/in-progress', {
      params: { year, month },
    }),
  getLines: (runId: string) =>
    apiClient.get<PayrollLineResponse[]>(`/payroll/runs/${runId}/lines`),
  // T17 — Audit Trail: field-level changes to payroll lines between draft
  // and approval (who changed what, old -> new).
  getChangeLog: (runId: string) =>
    apiClient.get<PayrollChangeLogEntry[]>(`/payroll/runs/${runId}/change-log`),
  getPreflight: (runId: string) =>
    apiClient.get<PreflightReport>(`/payroll/runs/${runId}/preflight`),
  downloadPayslip: (runId: string, employeeId: string) =>
    apiClient.get(`/payroll/runs/${runId}/payslip/${employeeId}/pdf`, {
      responseType: 'blob',
    }),
  // One-click payroll: create + calculate in one call. Optional
  // `operated_company_ids` restricts the paymaster scope (default =
  // all eligible operated cos; pass [] to skip operated cos entirely).
  runFull: (data: {
    period_year: number;
    period_month: number;
    company_id?: string;
    operated_company_ids?: string[];
    // GAP-007 — restrict the run to these employees (omit = whole roster).
    included_employee_ids?: string[];
  }) => apiClient.post('/payroll/run-full', data),
  // Phase 7A: Readiness check
  checkReadiness: (params: { year: number; month: number }) =>
    apiClient.get<PayrollReadinessResponse>('/payroll/readiness', { params }),
  // Phase 7A: Reconciliation
  getReconciliation: (runId: string) =>
    apiClient.get<PayrollReconciliationResponse>(`/payroll/runs/${runId}/reconciliation`),
  // Phase 7A: Bulk payslip distribution
  distributePayslips: (runId: string) =>
    apiClient.post<PayslipDistributionResult>(`/payroll/runs/${runId}/distribute-payslips`),
  // D2: Payroll → GL reconciliation
  getGLReconciliation: (runId: string) =>
    apiClient.get<PayrollGLReconResponse>(`/payroll/runs/${runId}/gl-reconciliation`),
  // Bank file export — download a bank-portal-ready salary file in the
  // requested format (nibss, gaps, primus, zenith, uba).
  downloadBankFile: (runId: string, format: string): Promise<void> =>
    apiClient
      .get(`/payroll/runs/${runId}/export/bank-file`, {
        params: { fmt: format },
        responseType: 'blob',
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data as BlobPart]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `payroll-${format}-${runId.slice(0, 8)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }),
};

// ============================================================
// PHASE 7A: STATUTORY REMITTANCE TRACKER
// ============================================================
export interface RemittanceResponse {
  id: string;
  company_id: string;
  obligation_id: string;
  obligation_type: string | null;
  authority: string | null;
  payroll_run_id: string | null;
  period_year: number;
  period_month: number;
  amount_due: string;
  amount_paid: string | null;
  due_date: string;
  payment_date: string | null;
  payment_reference: string | null;
  receipt_note: string | null;
  status: string;
  created_at: string;
}

export interface RemittanceSummaryRow {
  obligation_type: string;
  authority: string;
  months: Record<number, {
    id: string;
    amount_due: string;
    amount_paid: string | null;
    status: string;
    due_date: string;
  } | null>;
}

export interface ObligationResponse {
  id: string;
  company_id: string;
  obligation_type: string;
  authority: string;
  due_day: number;
  penalty_description: string | null;
  is_active: boolean;
}

export interface SirsState {
  state_code: string;
  state_name: string;
  abbreviation: string;
  sirs_name: string;
  employee_count: number;
  /** Total PAYE owed to this state's IRS (string-encoded decimal from server). */
  paye_total: string;
  is_fct: boolean;
}

/** One state SIRS (or the federal NRS) line in the PAYE-by-state report. */
export interface PayeByStateRow {
  state_code: string;
  state_name: string;
  abbreviation: string;
  sirs_name: string;
  is_federal: boolean;
  is_fct: boolean;
  amount_due: string;
  amount_paid: string;
  amount_outstanding: string;
  status: 'paid' | 'partial' | 'pending';
  earliest_due_date: string | null;
  period_count: number;
  employee_count: number;
}

/** Preview of free-text state → SIRS mapping before backfilling tax_state_code. */
export interface StateRoutingPreview {
  mappings: { state_code: string; state_name: string; abbreviation: string; employee_count: number }[];
  resolvable_count: number;
  unresolved_count: number;
  missing_total: number;
}

/** A state SIRS (or federal NRS) PAYE authority + its Remita biller config. */
export interface PayeAuthority {
  id: string;
  state_code: string | null;
  authority_name: string;
  is_federal: boolean;
  remittance_adapter: string;
  remita_service_type_id: string | null;
  is_configured: boolean;
  is_active: boolean;
}

/** Canonical NG PAYE tax-state option for employee assignment dropdowns. */
export interface TaxStateOption {
  state_code: string;
  state_name: string;
  abbreviation: string;
  sirs_name: string;
  is_fct: boolean;
}

export interface PayeByStateReport {
  year: number;
  month: number | null;
  states: PayeByStateRow[];
  totals: {
    amount_due: string;
    amount_paid: string;
    amount_outstanding: string;
    state_count: number;
    unassigned_employee_count: number;
  };
}

export const remittanceApi = {
  list: (params?: { year?: number; status?: string }) =>
    apiClient.get<RemittanceResponse[]>('/remittances', { params }),
  getSummary: (year: number) =>
    apiClient.get<RemittanceSummaryRow[]>('/remittances/summary', { params: { year } }),
  createManual: (data: { obligation_type: string; period_year: number; period_month: number; amount_due: number }) =>
    apiClient.post<RemittanceResponse>('/remittances', data),
  markPaid: (id: string, data: { amount_paid: number; payment_date: string; payment_reference?: string; receipt_note?: string }) =>
    apiClient.post<RemittanceResponse>(`/remittances/${id}/pay`, data),
  listObligations: () =>
    apiClient.get<ObligationResponse[]>('/remittances/obligations'),
  // State IRS PAYE schedule export (P01) — all 36 states + FCT, upload-ready CSV.
  // Keyed by period. authority = state_code ('RI'), abbreviation ('RIRS'), or legacy 'LIRS'/'FCT-IRS'.
  // Read X-Missing-Tax-State header to warn about employees without a state assignment.
  downloadIrsExport: (year: number, month: number, authority: string) =>
    apiClient.get<Blob>('/payroll/irs-export', {
      params: { year, month, authority },
      responseType: 'blob',
    }),
  // Returns which SIRS states are present in a payroll run (for dynamic state picker).
  getPayrollRunSirsStates: (runId: string) =>
    apiClient.get<SirsState[]>(`/payroll/runs/${runId}/sirs-states`),
  // Returns which SIRS states are present for an approved run in a given period.
  getPayrollPeriodSirsStates: (year: number, month: number) =>
    apiClient.get<SirsState[]>('/payroll/sirs-states', { params: { year, month } }),
  // Always-on PAYE-by-state + NRS federal report. Aggregates the remittance
  // ledger by routing state — independent of any single run. Omit month for the
  // full year; pass month to scope to one period.
  getPayeByState: (year: number, month?: number) =>
    apiClient.get<PayeByStateReport>('/payroll/paye-by-state', {
      params: month ? { year, month } : { year },
    }),
  // Canonical NG PAYE tax-state list (36 states + FCT) for employee assignment.
  listTaxStates: () => apiClient.get<TaxStateOption[]>('/payroll/tax-states'),
  // Preview/apply backfill of tax_state_code from each employee's free-text state.
  previewStateRouting: () =>
    apiClient.get<StateRoutingPreview>('/payroll/state-routing/preview'),
  applyStateRouting: () =>
    apiClient.post<{ updated: number; unresolved: number }>('/payroll/state-routing/apply'),
  // State PAYE authority config (Remita biller IDs) — READ-ONLY for tenants.
  // Editing/seeding moved to the operator console (platform_state_tax): these
  // biller IDs are platform-global money routing, no longer tenant-editable.
  listPayeAuthorities: () =>
    apiClient.get<PayeAuthority[]>('/payroll/statutory-authorities'),
  // PCRS pension schedule (P02) — PenCom upload-ready CSV.
  // Keyed by period. Read X-Missing-RSA-Pin header to warn about incomplete employees.
  downloadPcrsSchedule: (year: number, month: number) =>
    apiClient.get<Blob>('/payroll/pcrs-schedule', {
      params: { year, month },
      responseType: 'blob',
    }),
  /** Compliance pack ZIP (P05) — PCRS + all state IRS CSVs + manifest for a period. */
  downloadCompliancePack: (year: number, month: number) =>
    apiClient.get<Blob>('/payroll/compliance-pack', {
      params: { year, month },
      responseType: 'blob',
    }),
};

// Earned Wage Access (P07)
export interface EwaEligibility {
  basic_salary: string;
  earned_to_date: string;
  max_draw_pct: string;
  eligible: string;
  already_drawn: string;
  available_to_draw: string;
  period_year: number;
  period_month: number;
}

export interface EwaDrawRequestRow {
  id: string;
  employee_name: string;
  draw_amount: string;
  eligible_at_request_time: string;
  period: string;
  status: string;
  created_at: string;
}

export const ewaApi = {
  getEligible: () => apiClient.get<EwaEligibility>('/hr/ewa/eligible'),
  createDrawRequest: (amount: number) =>
    apiClient.post('/hr/ewa/draw-request', { amount }),
  getAdminRequests: (status?: string) =>
    apiClient.get<{ items: EwaDrawRequestRow[] }>('/hr/ewa/draw-requests', { params: status ? { status } : {} }),
  approveRequest: (id: string) => apiClient.post(`/hr/ewa/draw-requests/${id}/approve`, {}),
  rejectRequest: (id: string) => apiClient.post(`/hr/ewa/draw-requests/${id}/reject`, {}),
};

// ============================================================
// PHASE 7A: PAYROLL READINESS & RECONCILIATION TYPES
// ============================================================
export interface AffectedEmployee {
  id: string;
  name: string;
}

export interface ReadinessItem {
  check: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  affected_employees: AffectedEmployee[];
}

export interface PayrollReadinessResponse {
  ready: boolean;
  items: ReadinessItem[];
  total_employees: number;
  warnings: number;
  failures: number;
}

export interface ReconciliationLine {
  employee_id: string;
  employee_name: string;
  employee_ref: string;
  current_gross: string;
  previous_gross: string | null;
  variance_amount: string | null;
  variance_pct: string | null;
  flagged: boolean;
  reason: string | null;
}

export interface PayrollReconciliationResponse {
  period: string;
  previous_period: string | null;
  total_current_gross: string;
  total_previous_gross: string | null;
  total_variance: string | null;
  flagged_count: number;
  lines: ReconciliationLine[];
}

export interface PayslipDistributionResult {
  sent: number;
  failed: number;
  skipped: number;
  total: number;
  errors: string[];
}

// D2 / Payroll→GL Reconciliation
export interface GLReconLine {
  label: string;
  account_code: string;
  expected_debit: number;
  expected_credit: number;
  actual_debit: number;
  actual_credit: number;
  variance_debit: number;
  variance_credit: number;
  status: 'matched' | 'variance' | 'not_posted';
}

export interface PayrollGLReconResponse {
  run_id: string;
  period: string;
  run_status: string;
  total_gross: number;
  total_net: number;
  journal_entry_id: string | null;
  posted_at: string | null;
  lines: GLReconLine[];
  summary: {
    matched: number;
    variance_count: number;
    not_posted_count: number;
    total_lines: number;
  };
}

// ============================================================
// PHASE 7A: HR COMPLIANCE DASHBOARD
// ============================================================
export interface ComplianceItem {
  check: string;
  status: 'green' | 'amber' | 'red';
  count: number;
  message: string;
  affected_employees: AffectedEmployee[];
}

export interface ComplianceDashboardResponse {
  overall_score: number;
  total_employees: number;
  items: ComplianceItem[];
  red_count: number;
  amber_count: number;
  green_count: number;
}

export const hrAnalyticsApi = {
  getHeadcount: (year: number, departmentId?: string) =>
    apiClient.get<HeadcountResponse>('/hr/analytics/headcount', { params: { year, department_id: departmentId } }),
};

export const complianceApi = {
  getDashboard: () =>
    apiClient.get<ComplianceDashboardResponse>('/hr/compliance'),
};

// ============================================================
// SPRINT 4: GARNISHMENT MANAGEMENT
// ============================================================
export interface GarnishmentResponse {
  id: string;
  employee_id: string;
  company_id: string;
  garnishment_type: string;
  court_order_number: string | null;
  court_name: string | null;
  order_date: string | null;
  garnisher_name: string;
  garnisher_account: string | null;
  total_amount: string | null;
  monthly_amount: string;
  amount_paid: string;
  max_pct_of_net: string;
  priority: number;
  start_date: string;
  end_date: string | null;
  status: string;
  notes: string | null;
  employee_name: string | null;
  employee_ref: string | null;
  remaining_balance: string | null;
  created_at: string;
}

export const garnishmentApi = {
  list: (params?: { employee_id?: string; status?: string }) =>
    apiClient.get<GarnishmentResponse[]>('/payroll/garnishments', { params }),
  create: (data: Record<string, unknown>) =>
    apiClient.post<GarnishmentResponse>('/payroll/garnishments', data),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch<GarnishmentResponse>(`/payroll/garnishments/${id}`, data),
  get: (id: string) =>
    apiClient.get<GarnishmentResponse>(`/payroll/garnishments/${id}`),
};

// ============================================================
// SPRINT 4: ORG CHART
// ============================================================
export interface OrgChartNode {
  id: string;
  name: string;
  employee_ref: string;
  designation: string | null;
  department: string;
  department_id: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  employment_status: string;
  supervisor_id: string | null;
  children: OrgChartNode[];
}

export interface OrgChartData {
  nodes: OrgChartNode[];
  total_employees: number;
  department_counts: Record<string, number>;
  max_depth: number;
}

export const orgChartApi = {
  get: (params?: { department_id?: string }) =>
    apiClient.get<OrgChartData>('/hr/org-chart', { params }),
  move: (employeeId: string, newSupervisorId: string | null) =>
    apiClient.post('/hr/org-chart/move', null, {
      params: { employee_id: employeeId, new_supervisor_id: newSupervisorId },
    }),
};

// ============================================================
// SPRINT 4: EMPLOYEE DOCUMENT REPOSITORY
// ============================================================
export interface EmployeeDocumentResponse {
  id: string;
  employee_id: string;
  company_id: string;
  title: string;
  category: string;
  description: string | null;
  file_name: string;
  file_size: number;
  mime_type: string;
  issue_date: string | null;
  expiry_date: string | null;
  is_expired: boolean;
  is_verified: boolean;
  verified_by: string | null;
  verified_at: string | null;
  is_confidential: boolean;
  created_at: string;
  employee_name: string | null;
  employee_ref: string | null;
}

export interface DocumentRequirementResponse {
  id: string;
  company_id: string;
  title: string;
  category: string;
  description: string | null;
  department_id: string | null;
  designation: string | null;
  is_mandatory: boolean;
  has_expiry: boolean;
  is_active: boolean;
  created_at: string;
}

export interface DocumentComplianceResponse {
  total_requirements: number;
  compliant_count: number;
  missing_count: number;
  expired_count: number;
  expiring_soon_count: number;
  items: Array<{
    requirement_id: string;
    requirement_title: string;
    category: string;
    is_mandatory: boolean;
    status: string;
    affected_employees: Array<{ id: string; name: string; ref: string; detail: string }>;
    count: number;
  }>;
}

export const employeeDocsApi = {
  list: (params?: { employee_id?: string; category?: string }) =>
    apiClient.get<EmployeeDocumentResponse[]>('/hr/documents', { params }),
  upload: (employeeId: string, formData: FormData) =>
    apiClient.post<EmployeeDocumentResponse>(`/hr/documents/${employeeId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  verify: (docId: string) =>
    apiClient.post<EmployeeDocumentResponse>(`/hr/documents/${docId}/verify`),
  delete: (docId: string) =>
    apiClient.delete(`/hr/documents/${docId}`),
  download: (docId: string) =>
    apiClient.get(`/hr/documents/${docId}/download`, { responseType: 'blob' }),
  // Requirements
  listRequirements: () =>
    apiClient.get<DocumentRequirementResponse[]>('/hr/document-requirements'),
  createRequirement: (data: Record<string, unknown>) =>
    apiClient.post<DocumentRequirementResponse>('/hr/document-requirements', data),
  deleteRequirement: (id: string) =>
    apiClient.delete(`/hr/document-requirements/${id}`),
  // Compliance
  checkCompliance: () =>
    apiClient.get<DocumentComplianceResponse>('/hr/document-compliance'),
};

// ============================================================
// ONBOARDING
// ============================================================

export interface OnboardingTemplateItem {
  id: string;
  template_id: string;
  title: string;
  description: string | null;
  task_type: 'checklist' | 'document' | 'form' | 'content' | 'approval';
  category: 'pre_start' | 'day_one' | 'week_one' | 'month_one';
  assignee_type: 'employee' | 'manager' | 'hr' | 'buddy' | 'it' | 'finance';
  is_required: boolean;
  due_day_offset: number;
  sort_order: number;
  document_name: string | null;
  content_url: string | null;
  content_id: string | null;
  form_fields: Record<string, unknown> | null;
}

export interface OnboardingTemplate {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  department_id: string | null;
  designation: string | null;
  is_default: boolean;
  is_active: boolean;
  auto_transition: string | null;
  items: OnboardingTemplateItem[];
  created_at: string;
  updated_at: string;
}

export interface OnboardingDocument {
  id: string;
  task_id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  status: 'uploaded' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
}

export interface OnboardingTask {
  id: string;
  instance_id: string;
  title: string;
  description: string | null;
  task_type: string;
  category: string;
  assignee_type: string;
  is_required: boolean;
  due_date: string | null;
  sort_order: number;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'overdue';
  completed_at: string | null;
  completed_by: string | null;
  document_name: string | null;
  content_url: string | null;
  form_fields: Record<string, unknown> | null;
  form_data: Record<string, unknown> | null;
  notes: string | null;
  documents: OnboardingDocument[];
}

export interface OnboardingInstance {
  id: string;
  employee_id: string;
  company_id: string;
  template_id: string;
  buddy_id: string | null;
  start_date: string;
  status: 'in_progress' | 'completed' | 'cancelled';
  completed_at: string | null;
  progress_pct: number;
  total_tasks: number;
  completed_tasks: number;
  tasks: OnboardingTask[];
  created_at: string;
}

export interface OnboardingInstanceSummary {
  id: string;
  employee_id: string;
  employee_name: string;
  employee_ref: string;
  department_name: string | null;
  designation: string | null;
  template_name: string;
  buddy_name: string | null;
  start_date: string;
  status: string;
  progress_pct: number;
  total_tasks: number;
  completed_tasks: number;
  overdue_tasks: number;
  days_in_onboarding: number;
  created_at: string;
}

export interface OnboardingDashboardResponse {
  total_onboarding: number;
  avg_progress: number;
  overdue_count: number;
  avg_days: number;
  completed_this_month: number;
  instances: OnboardingInstanceSummary[];
  blocked_by: Record<string, number>;
  pending_doc_reviews: number;
}

export interface MyOnboardingTaskItem {
  id: string;
  instance_id: string;
  employee_id: string;
  employee_name: string;
  employee_ref: string;
  title: string;
  description: string | null;
  task_type: string;
  category: string;
  assignee_type: string;
  is_required: boolean;
  due_date: string | null;
  status: string;
  sort_order: number;
  notes: string | null;
  document_count: number;
  pending_docs: number;
}

export const onboardingApi = {
  // Templates
  listTemplates: (activeOnly = false) =>
    apiClient.get<{ items: OnboardingTemplate[]; total: number }>('/hr/onboarding/templates', { params: { active_only: activeOnly } }),
  getTemplate: (id: string) =>
    apiClient.get<OnboardingTemplate>(`/hr/onboarding/templates/${id}`),
  createTemplate: (data: Partial<OnboardingTemplate>) =>
    apiClient.post<OnboardingTemplate>('/hr/onboarding/templates', data),
  updateTemplate: (id: string, data: Partial<OnboardingTemplate>) =>
    apiClient.patch<OnboardingTemplate>(`/hr/onboarding/templates/${id}`, data),
  deleteTemplate: (id: string) =>
    apiClient.delete(`/hr/onboarding/templates/${id}`),

  // Template items
  addItem: (templateId: string, data: Partial<OnboardingTemplateItem>) =>
    apiClient.post<OnboardingTemplateItem>(`/hr/onboarding/templates/${templateId}/items`, data),
  updateItem: (templateId: string, itemId: string, data: Partial<OnboardingTemplateItem>) =>
    apiClient.patch<OnboardingTemplateItem>(`/hr/onboarding/templates/${templateId}/items/${itemId}`, data),
  deleteItem: (templateId: string, itemId: string) =>
    apiClient.delete(`/hr/onboarding/templates/${templateId}/items/${itemId}`),

  // Instances
  getDashboard: () =>
    apiClient.get<OnboardingDashboardResponse>('/hr/onboarding/dashboard'),
  startOnboarding: (data: { employee_id: string; template_id?: string; buddy_id?: string; start_date?: string }) =>
    apiClient.post<OnboardingInstance>('/hr/onboarding/instances', data),
  getInstance: (id: string) =>
    apiClient.get<OnboardingInstance>(`/hr/onboarding/instances/${id}`),
  cancelOnboarding: (id: string) =>
    apiClient.post<OnboardingInstance>(`/hr/onboarding/instances/${id}/cancel`),
  getMyOnboarding: () =>
    apiClient.get<OnboardingInstance | null>('/hr/onboarding/my-onboarding'),

  // My tasks (for managers, IT, buddies, HR)
  getMyTasks: (status?: string) =>
    apiClient.get<MyOnboardingTaskItem[]>('/hr/onboarding/my-tasks', { params: status ? { status } : {} }),

  // Tasks
  completeTask: (taskId: string, data?: { notes?: string; form_data?: Record<string, unknown> }) =>
    apiClient.post<OnboardingTask>(`/hr/onboarding/tasks/${taskId}/complete`, data || {}),
  skipTask: (taskId: string, reason: string) =>
    apiClient.post<OnboardingTask>(`/hr/onboarding/tasks/${taskId}/skip`, { reason }),
  addNote: (taskId: string, note: string) =>
    apiClient.post<OnboardingTask>(`/hr/onboarding/tasks/${taskId}/note`, { note }),

  // Documents
  uploadDocument: (taskId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<OnboardingDocument>(`/hr/onboarding/tasks/${taskId}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  reviewDocument: (docId: string, approved: boolean, rejectionReason?: string) =>
    apiClient.post<OnboardingDocument>(`/hr/onboarding/documents/${docId}/review`, {
      approved, rejection_reason: rejectionReason,
    }),

  // Content library
  listContent: () =>
    apiClient.get<Array<{ id: string; title: string; description: string | null; content_type: string; url: string | null; is_active: boolean; created_at: string }>>('/hr/onboarding/content'),
  createContent: (data: { title: string; content_type: string; url?: string; description?: string }) =>
    apiClient.post('/hr/onboarding/content', data),
  updateContent: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/hr/onboarding/content/${id}`, data),
  deleteContent: (id: string) =>
    apiClient.delete(`/hr/onboarding/content/${id}`),
};

// ============================================================
// EMPLOYEE DEDUCTIONS (Loans, Advances, Cooperative)
// ============================================================

export interface DeductionResponse {
  id: string;
  employee_id: string;
  company_id: string;
  deduction_type: 'loan' | 'advance' | 'cooperative' | 'savings' | 'custom' | 'withholding';
  description: string | null;
  total_amount: string;
  monthly_amount: string;
  remaining_balance: string;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Savings fields
  calculation_method: 'fixed' | 'percentage' | null;
  percentage: string | null;
  accumulated_amount: string;
  // Withholding fields (paywhld3x7m)
  pays_out_on_exit: boolean;
  payout_paid_at: string | null;
  consent_document_url: string | null;
  consent_signed_at: string | null;
  // Computed
  employee_name: string | null;
  employee_ref: string | null;
  amount_paid: string | null;
  installments_remaining: number | null;
}

export interface DeductionCreate {
  employee_id: string;
  deduction_type: 'loan' | 'advance' | 'cooperative' | 'savings' | 'custom';
  description?: string;
  total_amount: number;
  monthly_amount: number;
  start_date?: string;
  end_date?: string;
  calculation_method?: 'fixed' | 'percentage';
  percentage?: number;
}

export interface DeductionUpdate {
  description?: string;
  monthly_amount?: number;
  end_date?: string | null;
  is_active?: boolean;
  calculation_method?: 'fixed' | 'percentage';
  percentage?: number;
  consent_document_url?: string;
  consent_signed_at?: string;
}

export const deductionApi = {
  list: (params?: { employee_id?: string; deduction_type?: string; is_active?: boolean; page?: number; per_page?: number }) =>
    apiClient.get<{ items: DeductionResponse[]; total: number; page: number; per_page: number }>('/hr/deductions', { params }),
  get: (id: string) =>
    apiClient.get<DeductionResponse>(`/hr/deductions/${id}`),
  create: (data: DeductionCreate) =>
    apiClient.post<DeductionResponse>('/hr/deductions', data),
  update: (id: string, data: DeductionUpdate) =>
    apiClient.patch<DeductionResponse>(`/hr/deductions/${id}`, data),
  delete: (id: string) =>
    apiClient.delete(`/hr/deductions/${id}`),
};

// ============================================================
// PAYROLL WITHHOLDINGS — retention-style deduction refunded on exit
// ============================================================

export interface WithholdingCreate {
  employee_id: string;
  percentage: number;
  duration_months: number;
  consent_document_url: string;
  consent_signed_at: string;
  description?: string;
  start_date?: string;
}

export interface WithholdingReleaseResponse {
  released_count: number;
  total_released: string;
  items: {
    deduction_id: string;
    description: string | null;
    accumulated_amount: string;
    paid_at: string;
  }[];
}

export const withholdingApi = {
  create: (data: WithholdingCreate) =>
    apiClient.post<DeductionResponse>('/hr/withholdings', data),
  release: (employeeId: string) =>
    apiClient.post<WithholdingReleaseResponse>(
      `/hr/withholdings/release/${employeeId}`,
    ),
};

// ============================================================
// LOAN REQUESTS
// ============================================================

export interface LoanRequestResponse {
  id: string;
  employee_id: string;
  company_id: string;
  amount_requested: string;
  proposed_monthly_repayment: string;
  purpose: string;
  status: 'pending' | 'approved' | 'rejected' | 'disbursed' | 'cancelled';
  approved_amount: string | null;
  approved_monthly_repayment: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  deduction_id: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  employee_name: string | null;
  employee_ref: string | null;
  installments: number | null;
}

export interface LoanRequestCreate {
  amount_requested: number;
  proposed_monthly_repayment: number;
  purpose: string;
}

export interface LoanRequestReview {
  approved: boolean;
  approved_amount?: number;
  approved_monthly_repayment?: number;
  review_notes?: string;
}

export const loanRequestApi = {
  list: (params?: { employee_id?: string; status?: string; page?: number; per_page?: number }) =>
    apiClient.get<{ items: LoanRequestResponse[]; total: number; page: number; per_page: number }>('/hr/loan-requests', { params }),
  get: (id: string) =>
    apiClient.get<LoanRequestResponse>(`/hr/loan-requests/${id}`),
  review: (id: string, data: LoanRequestReview) =>
    apiClient.post<LoanRequestResponse>(`/hr/loan-requests/${id}/review`, data),
};

export interface LoanPolicy {
  monthly_basic_salary: number;
  max_loan_amount: number;
  max_loan_salary_multiple: number;
  max_loan_term_months: number;
}

export const portalFinanceApi = {
  getMyFinances: () =>
    apiClient.get<{ deductions: DeductionResponse[]; loan_requests: LoanRequestResponse[]; loan_policy?: LoanPolicy }>('/employee/my-finances'),
  getMyWithholdings: () =>
    apiClient.get<{ items: DeductionResponse[] }>('/employee/my-withholdings'),
  submitLoanRequest: (data: LoanRequestCreate) =>
    apiClient.post<LoanRequestResponse>('/employee/loan-request', data),
  cancelLoanRequest: (id: string) =>
    apiClient.post<LoanRequestResponse>(`/employee/loan-request/${id}/cancel`),
};

// ============================================================
// EMPLOYEE "MY TAX" — self-service YTD statutory position + tax certificate
// ============================================================

export interface StaffStatutoryYtdResponse {
  scope: 'staff';
  employee_id: string;
  company_id: string;
  tax_year: number;
  // Serialized Decimal-as-string component map.
  totals: {
    gross_pay: string;
    paye: string;
    pension_employee: string;
    pension_employer: string;
    nhf: string;
    nsitf: string;
    itf: string;
    net_pay: string;
  };
  effective_tax_rate_pct: string;
  profile_hashes: string[];
  last_period_end: string | null;
  components: Record<string, unknown>;
  country_code: string;
}

export const myTaxApi = {
  /** YTD PAYE + statutory position for an employee (self-service: server
   *  enforces is_self for users holding only payroll.read). */
  getStaffYtd: (employeeId: string, taxYear?: number) =>
    apiClient.get<StaffStatutoryYtdResponse>(
      `/payroll/statutory-position/staff/${employeeId}`,
      { params: taxYear ? { tax_year: taxYear } : undefined },
    ),
  /** Download own statutory tax-deduction certificate (PDF). Self-only —
   *  the endpoint derives the employee from the auth token, no id param. */
  downloadTaxCertificate: (taxYear: number) =>
    apiClient.get('/payroll/me/tax-certificate', {
      params: { tax_year: taxYear },
      responseType: 'blob',
    }),
};

// ============================================================
// EMPLOYEE ALLOWANCES (dynamic)
// ============================================================

export interface EmployeeAllowanceResponse {
  id: string;
  employee_id: string;
  company_id: string;
  allowance_type_id: string;
  allowance_type_name: string | null;
  allowance_type_code: string | null;
  amount: number;
  is_active: boolean;
  created_at: string | null;
}

export const employeeAllowancesApi = {
  list: (employeeId: string) =>
    apiClient.get<EmployeeAllowanceResponse[]>(`/hr/employees/${employeeId}/allowances`),
  create: (employeeId: string, data: { allowance_type_id: string; amount: number }) =>
    apiClient.post<EmployeeAllowanceResponse>(`/hr/employees/${employeeId}/allowances`, data),
  update: (employeeId: string, id: string, data: { amount?: number; is_active?: boolean }) =>
    apiClient.patch<EmployeeAllowanceResponse>(`/hr/employees/${employeeId}/allowances/${id}`, data),
  delete: (employeeId: string, id: string) =>
    apiClient.delete(`/hr/employees/${employeeId}/allowances/${id}`),
};

// ============================================================
// EMPLOYEE NOTES (HR-only)
// ============================================================

export interface EmployeeNoteResponse {
  id: string;
  employee_id: string;
  company_id: string;
  note_type: 'general' | 'performance' | 'medical' | 'confidential';
  note_text: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const employeeNotesApi = {
  list: (employeeId: string, noteType?: string) =>
    apiClient.get<EmployeeNoteResponse[]>(`/hr/employees/${employeeId}/notes`, { params: { note_type: noteType } }),
  create: (employeeId: string, data: { note_type: string; note_text: string }) =>
    apiClient.post<EmployeeNoteResponse>(`/hr/employees/${employeeId}/notes`, data),
  update: (employeeId: string, id: string, data: { note_type?: string; note_text?: string }) =>
    apiClient.patch<EmployeeNoteResponse>(`/hr/employees/${employeeId}/notes/${id}`, data),
  delete: (employeeId: string, id: string) =>
    apiClient.delete(`/hr/employees/${employeeId}/notes/${id}`),
};

// ============================================================
// DISCIPLINARY MODULE
// ============================================================

export interface DisciplinaryOutcomeResponse {
  id: string;
  case_id: string;
  company_id: string;
  hearing_id: string | null;
  outcome_type: string;
  details: string | null;
  effective_date: string;
  end_date: string | null;
  fine_amount: number | null;
  deduction_id: string | null;
  decided_by: string;
  decided_by_name: string | null;
  is_escalation: boolean;
  escalation_override_reason: string | null;
  created_at: string | null;
}

export interface DisciplinaryAppealResponse {
  id: string;
  case_id: string;
  company_id: string;
  appeal_date: string;
  grounds: string;
  appeal_officer_id: string | null;
  appeal_officer_name: string | null;
  decision: string | null;
  decision_date: string | null;
  decision_notes: string | null;
  new_outcome_id: string | null;
  created_at: string | null;
}

export interface DisciplinaryCaseResponse {
  id: string;
  employee_id: string;
  employee_name: string | null;
  employee_ref: string | null;
  company_id: string;
  case_number: string;
  violation_category_id: string | null;
  violation_category_name: string | null;
  severity: string;
  incident_date: string;
  reported_date: string;
  reported_by: string;
  reported_by_name: string | null;
  description: string;
  evidence_notes: string | null;
  status: string;
  closed_at: string | null;
  closed_reason: string | null;
  created_at: string | null;
  hearings_count: number;
  outcomes: DisciplinaryOutcomeResponse[];
  appeals: DisciplinaryAppealResponse[];
}

export interface HearingResponse {
  id: string;
  case_id: string;
  company_id: string;
  hearing_date: string;
  hearing_officer_id: string;
  hearing_officer_name: string | null;
  panel_members: { user_id: string; name: string; role: string }[];
  location: string | null;
  employee_present: boolean;
  employee_statement: string | null;
  findings: string | null;
  status: string;
  created_at: string | null;
}

export interface ExitSeveranceItem {
  type_code: string;
  type_name: string;
  gross_amount: string;
  exempt_amount: string;
  taxable_amount: string;
  tax_amount: string;
  net_amount: string;
}

export interface ExitSeveranceCalculation {
  employee_id: string;
  exit_date: string;
  profile_slug: string;
  profile_hash: string;
  items: ExitSeveranceItem[];
  total_gross: string;
  total_exempt: string;
  total_taxable: string;
  total_tax: string;
  total_net: string;
}

export interface ExitDraftSummary {
  id: string;
  employee_id: string;
  employee_name: string;
  exit_date: string;
  exit_reason: string;
  status: string;
  total_gross: string;
  submitted_by: string;
  created_at: string;
}

export interface EmployeeDisciplineSummary {
  total_cases: number;
  active_cases: number;
  warnings_count: number;
  suspensions_count: number;
  fines_total: number;
  highest_active_warning: string | null;
  cases: DisciplinaryCaseResponse[];
}

export interface DocAnomalyFlag {
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  doc_id: string | null;
  category: string;
  message: string;
}

export interface PayrollAIRunLevelCheck {
  check: string;
  result: 'PASS' | 'FAIL' | 'WARN';
  detail: string;
}

export interface PayrollAIAnomaly {
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  employee_ref: string;
  check: string;
  finding: string;
  recommendation: string;
}

export interface PayrollAIReview {
  overall_risk: 'LOW' | 'MEDIUM' | 'HIGH';
  summary: string;
  anomalies: PayrollAIAnomaly[];
  run_level_checks: PayrollAIRunLevelCheck[];
}

export const disciplinaryApi = {
  // Cases
  listCases: (params?: { employee_id?: string; status?: string; page?: number; per_page?: number }) =>
    apiClient.get<{ items: DisciplinaryCaseResponse[]; total: number; page: number; per_page: number }>('/hr/disciplinary/cases', { params }),
  getCase: (id: string) =>
    apiClient.get<DisciplinaryCaseResponse>(`/hr/disciplinary/cases/${id}`),
  createCase: (data: {
    employee_id: string; violation_category_id?: string; severity: string;
    incident_date: string; description: string; evidence_notes?: string;
  }) =>
    apiClient.post<DisciplinaryCaseResponse>('/hr/disciplinary/cases', data),
  updateCase: (id: string, data: {
    violation_category_id?: string; severity?: string; description?: string; evidence_notes?: string;
  }) =>
    apiClient.patch<DisciplinaryCaseResponse>(`/hr/disciplinary/cases/${id}`, data),
  transitionCase: (id: string, data: { new_status: string; reason?: string }) =>
    apiClient.post<DisciplinaryCaseResponse>(`/hr/disciplinary/cases/${id}/transition`, data),

  // Employee summary
  getEmployeeSummary: (employeeId: string) =>
    apiClient.get<EmployeeDisciplineSummary>(`/hr/disciplinary/employees/${employeeId}/summary`),

  // Hearings
  createHearing: (caseId: string, data: {
    hearing_date: string; hearing_officer_id: string; panel_members?: unknown[]; location?: string;
  }) =>
    apiClient.post<HearingResponse>(`/hr/disciplinary/cases/${caseId}/hearings`, data),
  updateHearing: (id: string, data: Partial<{
    hearing_date: string; hearing_officer_id: string; panel_members: unknown[];
    location: string; employee_present: boolean; employee_statement: string;
    findings: string; status: string;
  }>) =>
    apiClient.patch<HearingResponse>(`/hr/disciplinary/hearings/${id}`, data),

  // Outcomes
  createOutcome: (caseId: string, data: {
    outcome_type: string; effective_date: string; hearing_id?: string;
    details?: string; end_date?: string; fine_amount?: number;
    escalation_override_reason?: string;
  }) =>
    apiClient.post<DisciplinaryOutcomeResponse>(`/hr/disciplinary/cases/${caseId}/outcomes`, data),

  // Appeals
  createAppeal: (caseId: string, data: {
    appeal_date: string; grounds: string; appeal_officer_id?: string;
  }) =>
    apiClient.post<DisciplinaryAppealResponse>(`/hr/disciplinary/cases/${caseId}/appeals`, data),
  decideAppeal: (id: string, data: {
    decision: string; decision_notes?: string; new_outcome?: unknown;
  }) =>
    apiClient.post<DisciplinaryAppealResponse>(`/hr/disciplinary/appeals/${id}/decide`, data),

};

export const exitWizardApi = {
  calculateSeverance: (params: {
    employee_id: string; exit_date: string;
    amounts: Record<string, number>;
  }) =>
    apiClient.post<ExitSeveranceCalculation>(`/hr/exit-wizard/calculate?employee_id=${params.employee_id}&exit_date=${params.exit_date}`, { amounts: params.amounts }),
  submitExit: (data: {
    employee_id: string; exit_date: string; exit_reason: string;
    amounts: Record<string, number>; notes?: string;
  }) =>
    apiClient.post<{ draft_id: string; status: string; message: string }>('/hr/exit-wizard/submit', data),
  finalizeExit: (draftId: string) =>
    apiClient.post<{ draft_id: string; status: string; message: string }>(`/hr/exit-wizard/${draftId}/finalize`, {}),
  listExitDrafts: (status?: string) =>
    apiClient.get<ExitDraftSummary[]>(`/hr/exit-wizard/drafts${status ? `?status=${status}` : ''}`),
};

export const aiDocApi = {
  analyseDocument: (documentId: string) =>
    apiClient.post<{ status: string; structured?: Record<string, unknown> }>(
      `/hr/doc-intelligence/documents/${documentId}/analyse`
    ),
  getEmployeeDocSummary: (employeeId: string) =>
    apiClient.get<{
      employee_id: string;
      total_docs: number;
      anomalies: DocAnomalyFlag[];
      critical_count: number;
      warning_count: number;
    }>(`/hr/doc-intelligence/employees/${employeeId}/summary`),
  getDocIntelligenceAskUrl: (employeeId: string) =>
    `/hr/doc-intelligence/employees/${employeeId}/ask`,
  triggerPayrollAIReview: (runId: string) =>
    apiClient.post<{ status: string; review: PayrollAIReview }>(`/payroll/ai-review/runs/${runId}/review`),
  getPayrollAIReview: (runId: string) =>
    apiClient.get<{
      run_id: string;
      ai_review_status: string;
      ai_review_json: PayrollAIReview;
      ai_review_completed_at: string | null;
      ai_review_acknowledged_at: string | null;
    }>(`/payroll/ai-review/runs/${runId}/review`),
  acknowledgePayrollAIReview: (runId: string) =>
    apiClient.post<{ status: string; run_id: string }>(`/payroll/ai-review/runs/${runId}/acknowledge`),
};

// ------------------------------------------------------------------ //
// P06 — WHT Remittance Schedules
// ------------------------------------------------------------------ //

export interface WHTScheduleRow {
  id: string;
  period_year: number;
  period_month: number;
  authority: string;
  total_wht_due: string;
  line_count: number;
  due_date: string | null;
  status: 'pending' | 'paid' | 'overdue';
  payment_reference: string | null;
  remittance_adapter: string;
  paid_at: string | null;
}

export const whtApi = {
  listSchedules: () =>
    apiClient.get<WHTScheduleRow[]>('/wht-schedules'),
  paySchedule: (scheduleId: string) =>
    apiClient.post<{
      schedule_id: string;
      authority: string;
      amount: string;
      payment_reference: string;
      status: string;
    }>(`/wht-schedules/${scheduleId}/pay`),
};
